import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import React, { useEffect, useRef, useState } from "react";
import { getFormByFormID } from "./fetchData";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { convertEpochtoDate } from "../../../../helper/DateConverters";

const ViewGloucesterForm = () => {
  const toast = useRef<Toast>(null);
  const ImageNotAvailable = process.env.REACT_APP_IMAGE_NOT_FOUND_URL || "";
  const [formData, setFormData] = useState<any>(null);
  const [formDataLoading, setFormDataLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImgSrc, setModalImgSrc] = useState<string | null>(null);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [imageTransform, setImageTransform] = useState({
    scale: 1,
    rotation: 0,
  });

  // Handle modal open with proper state management
  const handleModalOpen = (imageSrc: string) => {
    setModalImgSrc(imageSrc);
    setModalOpen(true);
    setIsModalClosing(false);
    // Reset transform when opening modal
    setImageTransform({ scale: 1, rotation: 0 });
  };

  // Handle modal close with animation
  const handleModalClose = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setModalOpen(false);
      setIsModalClosing(false);
      setModalImgSrc(null);
      // Reset transform when closing modal
      setImageTransform({ scale: 1, rotation: 0 });
    }, 300); // Match animation duration
  };

  // Add keyframe animations to head
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerHTML = `
          @keyframes fadeIn {
            0% { 
              opacity: 0; 
              visibility: visible;
            }
            100% { 
              opacity: 1; 
              visibility: visible;
            }
          }
          @keyframes fadeOut {
            0% { 
              opacity: 1; 
              visibility: visible;
            }
            100% { 
              opacity: 0; 
              visibility: visible;
            }
          }
          @keyframes scaleIn {
            0% { 
              opacity: 0;
              transform: scale(0.8); 
              visibility: visible;
            }
            100% { 
              opacity: 1;
              transform: scale(1); 
              visibility: visible;
            }
          }
          @keyframes scaleOut {
            0% { 
              opacity: 1;
              transform: scale(1); 
              visibility: visible;
            }
            100% { 
              opacity: 0;
              transform: scale(0.8); 
              visibility: visible;
            }
          }
        `;
    document.head.appendChild(styleSheet);

    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  // Format date from timestamp
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date and time from timestamp
  const formatDateTime = (timestamp: number) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Modal component
  const ImageModal = ({
    open,
    src,
    onClose,
  }: {
    open: boolean;
    src: string | null;
    onClose: () => void;
  }) => {
    // Show modal if it's open OR if it's closing (to allow animation)
    const shouldShow = open || isModalClosing;

    if (!shouldShow || !src) return null;

    return (
      <div
        className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/70 tw-backdrop-blur-sm"
        style={{
          animation: isModalClosing
            ? "fadeOut 0.3s ease-out forwards"
            : "fadeIn 0.3s ease-out forwards",
          visibility: "visible",
        }}
        onClick={onClose}
      >
        <div
          className="tw-relative tw-bg-transparent tw-p-4 tw-transform"
          style={{
            animation: isModalClosing
              ? "scaleOut 0.3s ease-out forwards"
              : "scaleIn 0.3s ease-out forwards",
            visibility: "visible",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="tw-absolute tw-top-2 tw-right-2 tw-text-white tw-text-2xl tw-bg-black/50 tw-rounded-full tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center hover:tw-bg-black tw-transition-colors tw-duration-200"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={src}
            alt="Large Preview"
            className="tw-max-w-[90vw] tw-max-h-[80vh] tw-rounded tw-shadow-lg"
            style={{ visibility: "visible" }}
          />
        </div>
      </div>
    );
  };

  const formatTime = (timestamp: string | number) => {
    if (!timestamp) return "N/A";
    const timeStamp =
      typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
    return new Date(timeStamp * 1000).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getAmelioStaffNames = () => {
    if (!formData?.formJson?.amelioStaff) return "N/A";
    return formData.formJson.amelioStaff
      .map((staff: any) => staff.displayName)
      .join(", ");
  };

  const getImageByType = (type: string) => {
    if (!formData?.fileJson?.siteImages) return ImageNotAvailable;
    const image = formData.fileJson.siteImages.find((img: any) =>
      img.originalname.includes(type)
    );
    return image ? image.file : ImageNotAvailable;
  };

  const getSignatureByType = (type: string) => {
    if (!formData?.fileJson?.signature) return ImageNotAvailable;
    const signature = formData.fileJson.signature.find((sig: any) =>
      sig.originalname.includes(type)
    );
    return signature ? signature.file : ImageNotAvailable;
  };

  const query = new URLSearchParams(window.location.search);
  const formIDParam = Number(query.get("formID"));

  useEffect(() => {
    if (!formIDParam) {
      navigate("/layout/health-and-safety/jobsheet-gloucester");
    }
    getFormByFormID(
      setFormData,
      setFormDataLoading,
      formIDParam,
      navigate,
      toast
    );
  }, [formIDParam, navigate]);

  // Helper to get timestamp and location by photoKey
  const getPhotoMeta = (photoKey: string) => {
    const locationObj = formData?.timeStampLocationJson?.locations?.find(
      (loc: any) => loc.photoKey === photoKey
    );
    const timeObj = formData?.timeStampLocationJson?.timeStamps?.find(
      (t: any) => t.photoKey === photoKey
    );
    return {
      address: locationObj?.address || "",
      timestamp: timeObj?.timestamp || "",
    };
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />
      {formDataLoading ? (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-[50px] lg:tw-left-[120px] tw-z-50">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="tw-border-solid tw-border tw-border-[#E2E2e2] tw-rounded-lg tw-relative tw-bg-white">
          <div className="tw-bg-[#F8FAFC] tw-p-4 tw-rounded-t-lg tw-border-b-[1px] tw-border-[#E2E8F0] tw-flex tw-justify-between tw-items-center">
            <h2 className="tw-text-2xl tw-font-semibold tw-text-[#101828]">
              Gloucester Job Sheet
            </h2>
          </div>
          <div>
            <div className="tw-m tw-w-full tw-mb-[62px]">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-pb-2 tw-border-b tw-p-4 tw-border-gray-200">
                Basic Information
              </h3>
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-y-6 tw-gap-4 tw-px-4">
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Invoice No :</p>
                  <p className="leak-value">{formData?.invoiceNo || "N/A"}</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Amelio ID :</p>
                  <p className="leak-value">{formData?.amelioID || "N/A"}</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Job/Order No :</p>
                  <p className="leak-value">
                    {formData?.formJson?.jobOrderNo || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Invoice Value :</p>
                  <p className="leak-value">
                    {formData?.invoiceValue || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Date Issued :</p>
                  <p className="leak-value">
                    {formData?.dateIssued
                      ? formatDateTime(formData?.dateIssued)
                      : "N/A"}
                  </p>
                </div>
                {/* <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Target Completion date :</p>
                  <p className="leak-value">
                    {formData?.formJson?.targetCompletionDate
                      ? formatDate(formData?.formJson?.targetCompletionDate)
                      : "N/A"}
                  </p>
                </div> */}
              </div>
            </div>
            <Divider
              className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
              align="center"
            ></Divider>
            <div className="tw-w-full tw-mb-[62px]">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-pb-2 tw-border-b tw-px-4 tw-border-gray-200">
                Client Details
              </h3>
              <div className="tw-px-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-y-6  tw-gap-4">
                <div className="tw-flex tw-flex-col">
                  <p className="leak-detail-label">Client Name</p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.clientName || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-flex-col tw-gap-1">
                  <p className="leak-detail-label">Client Address</p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.clientAddress || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <Divider
              className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
              align="center"
            ></Divider>
            <div className="tw- tw-w-full tw-mb-[62px]">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-pb-2 tw-border-b tw-px-4 tw-border-gray-200">
                Site Details
              </h3>
              <div className=" tw-px-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3  tw-gap-4 tw-gap-y-6">
                <div className="tw-flex tw-flex-col ">
                  <span className="tw-text-sm tw-font-medium tw-text-gray-500">
                    Amelio Staff
                  </span>
                  <span className="tw-text-base tw-text-gray-800">
                    {getAmelioStaffNames()}
                  </span>
                </div>
                <div className="tw-flex tw-items-start tw-flex-col tw-gap-1">
                  <p className="leak-detail-label">Site Address</p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.siteAddress || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Date :</p>
                  <p className="leak-value">
                    {formData?.formJson?.date
                      ? formatDate(formData?.formJson?.date)
                      : "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Start time :</p>
                  <p className="leak-value">
                    {formData?.formJson?.startTime
                      ? formatTime(formData?.formJson?.startTime)
                      : "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Finish Time :</p>
                  <p className="leak-value">
                    {formData?.formJson?.finishTime
                      ? formatTime(formData?.formJson?.finishTime)
                      : "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <p className="leak-detail-label">Travel Time :</p>
                  <p className="leak-value">
                    {formData?.formJson?.travelTime || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Images Section */}
            {/* <Divider className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   " align="center">Site Images</Divider> */}
            <Divider
              className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
              align="center"
            ></Divider>
            <div className="tw-m tw-w-full tw-mb-[62px]">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-pb-2 tw-border-b tw-px-4 tw-border-gray-200">
                Site Images
              </h3>

              {/* Before Images */}
              <div className="tw-flex tw-flex-col tw-w-full tw-mb-6">
                <span className="tw-text-sm tw-font-bold tw-text-gray-700 tw-mb-2 tw-ml-4">
                  Before Images
                </span>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-2 tw-px-4">
                  {(
                    formData?.fileJson?.siteImages
                      ?.filter((img: any) =>
                        img.originalname.toLowerCase().includes("before")
                      )
                      // Sort by the number after "before-" in the filename
                      ?.sort((a: any, b: any) => {
                        const getNum = (name: string) => {
                          const match = name.match(/before-(\d+)/i);
                          return match ? parseInt(match[1], 10) : 0;
                        };
                        return getNum(a.originalname) - getNum(b.originalname);
                      }) || [ImageNotAvailable]
                  ).map((img: any, idx: number) => {
                    return (
                      <div
                        key={img?.file || idx}
                        className="tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-bg-gray-50 tw-flex tw-justify-center tw-w-full tw-max-h-40"
                      >
                        <img
                          src={img?.file || ImageNotAvailable}
                          alt={`before site ${idx + 1}`}
                          onClick={() => {
                            handleModalOpen(img?.file || ImageNotAvailable);
                          }}
                          onMouseEnter={(e) => {
                            const parent = (e.target as HTMLElement)
                              .parentElement;
                            if (parent) {
                              parent.style.position = "relative";
                              let iconDiv =
                                parent.querySelector(".hover-eye-icon");
                              if (!iconDiv) {
                                iconDiv = document.createElement("div");
                                iconDiv.className =
                                  "hover-eye-icon tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-ease-in-out";
                                iconDiv.innerHTML =
                                  '<i class="pi pi-eye tw-text-white tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-ease-in-out" style="background-color:#222;opacity:0.8;border-radius:0.5rem;transform:scale(0.9);"></i>';
                                (iconDiv as HTMLElement).style.pointerEvents =
                                  "none";
                                parent.appendChild(iconDiv);
                                setTimeout(() => {
                                  const icon = iconDiv!.querySelector("i");
                                  if (icon) {
                                    (icon as HTMLElement).style.transform =
                                      "scale(1)";
                                    (icon as HTMLElement).style.opacity = "0.9";
                                  }
                                }, 10);
                              }
                              const imgEl = parent.querySelector("img");
                              if (imgEl) {
                                (imgEl as HTMLElement).style.filter =
                                  "brightness(0.5)";
                                (imgEl as HTMLElement).style.transition =
                                  "filter 0.2s ease-in-out";
                              }
                            }
                          }}
                          onMouseLeave={(e) => {
                            const parent = (e.target as HTMLElement)
                              .parentElement;
                            if (parent) {
                              const iconDiv =
                                parent.querySelector(".hover-eye-icon");
                              if (iconDiv) parent.removeChild(iconDiv);
                              const imgEl = parent.querySelector("img");
                              if (imgEl) {
                                (imgEl as HTMLElement).style.filter = "";
                              }
                            }
                          }}
                          className="tw-h-24 tw-max-h-32 tw-object-contain tw-w-full tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-scale-[1.02]"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <Divider
                className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
                align="center"
              ></Divider>
              {/* After Images */}
              <div className="tw-flex tw-flex-col tw-w-full">
                <span className="tw-text-sm tw-font-bold tw-text-gray-700 tw-mb-2 tw-ml-4">
                  After Images
                </span>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-2 tw-px-4">
                  {(
                    formData?.fileJson?.siteImages
                      ?.filter((img: any) =>
                        img.originalname.toLowerCase().includes("after")
                      )
                      // Sort by the number after "after-" in the filename
                      ?.sort((a: any, b: any) => {
                        const getNum = (name: string) => {
                          const match = name.match(/after-(\d+)/i);
                          return match ? parseInt(match[1], 10) : 0;
                        };
                        return getNum(a.originalname) - getNum(b.originalname);
                      }) || [ImageNotAvailable]
                  ).map((img: any, idx: number) => {
                    return (
                      <div
                        key={img?.file || idx}
                        className="tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-bg-gray-50 tw-flex tw-justify-center tw-w-full tw-max-h-40"
                      >
                        <img
                          src={img?.file || ImageNotAvailable}
                          alt={`after site ${idx + 1}`}
                          onClick={() => {
                            handleModalOpen(img?.file || ImageNotAvailable);
                          }}
                          onMouseEnter={(e) => {
                            const parent = (e.target as HTMLElement)
                              .parentElement;
                            if (parent) {
                              parent.style.position = "relative";
                              let iconDiv =
                                parent.querySelector(".hover-eye-icon");
                              if (!iconDiv) {
                                iconDiv = document.createElement("div");
                                iconDiv.className =
                                  "hover-eye-icon tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-ease-in-out";
                                iconDiv.innerHTML =
                                  '<i class="pi pi-eye tw-text-white tw-h-10 tw-w-10 tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-ease-in-out" style="background-color:#222;opacity:0.8;border-radius:0.5rem;transform:scale(0.9);"></i>';
                                (iconDiv as HTMLElement).style.pointerEvents =
                                  "none";
                                parent.appendChild(iconDiv);
                                setTimeout(() => {
                                  const icon = iconDiv!.querySelector("i");
                                  if (icon) {
                                    (icon as HTMLElement).style.transform =
                                      "scale(1)";
                                    (icon as HTMLElement).style.opacity = "0.9";
                                  }
                                }, 10);
                              }
                              const imgEl = parent.querySelector("img");
                              if (imgEl) {
                                (imgEl as HTMLElement).style.filter =
                                  "brightness(0.5)";
                                (imgEl as HTMLElement).style.transition =
                                  "filter 0.2s ease-in-out";
                              }
                            }
                          }}
                          onMouseLeave={(e) => {
                            const parent = (e.target as HTMLElement)
                              .parentElement;
                            if (parent) {
                              const iconDiv =
                                parent.querySelector(".hover-eye-icon");
                              if (iconDiv) parent.removeChild(iconDiv);
                              const imgEl = parent.querySelector("img");
                              if (imgEl) {
                                (imgEl as HTMLElement).style.filter = "";
                              }
                            }
                          }}
                          className="tw-h-24 tw-max-h-32 tw-object-contain tw-w-full tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-scale-[1.02]"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* <Divider className="tw-my-2 tw-bg-gray-300 tw-h-[1px]" align="center">
              Plant{" "}
            </Divider> */}
            <Divider
              className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
              align="center"
            ></Divider>
            <div className="tw-mb-8 tw-w-full">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4 tw-pb-2 tw-border-b tw-px-4 tw-border-gray-200">
                Plant
              </h3>
              <div className=" tw-px-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2  tw-gap-4">
                <div className="tw-flex tw-items-center  tw-gap-1">
                  <p className="leak-detail-label">Vehicle Type :</p>
                  <p className="leak-value">
                    {formData?.formJson?.vehicleType || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center  tw-gap-1">
                  <p className="leak-detail-label">Vehicle Registration :</p>
                  <p className="leak-value">
                    {formData?.formJson?.vehicleRegistration || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-flex-col  tw-gap-1">
                  <p className="leak-detail-label">Job Description</p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.jobDescription || "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-flex-col  tw-gap-1">
                  <p className="leak-detail-label">Works Carried out</p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.worksCarriedOut || "N/A"}
                  </p>
                </div>

                {/* <div className="tw-flex tw-items-center  tw-gap-1">
                  <p className="leak-detail-label">
                    Return visit Start date and Time :
                  </p>
                  <p className="leak-value">
                    {formData?.formJson?.returnVisitStartDateTime
                      ? formatDateTime(
                          formData?.formJson?.returnVisitStartDateTime
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-center  tw-gap-1">
                  <p className="leak-detail-label">
                    Return Visit Finish Date and Time:
                  </p>
                  <p className="leak-value">
                    {formData?.formJson?.returnVisitFinishDateTime
                      ? formatDateTime(
                          formData?.formJson?.returnVisitFinishDateTime
                        )
                      : "N/A"}
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-flex-col  tw-gap-1">
                  <p className="leak-detail-label">Remedial Action </p>
                  <p className="leak-value tw-break-words tw-max-w-full tw-whitespace-normal">
                    {formData?.formJson?.remedialAction || "N/A"}
                  </p>
                </div> */}
              </div>
            </div>
            {/* Signatures Section */}
            {/* <Divider className="tw-my-2 tw-bg-gray-300 tw-h-[1px]" align="center">
              Signatures{" "}
            </Divider> */}
            <Divider
              className="tw-my-2 tw-bg-gray-300 tw-h-[1px]   "
              align="center"
            ></Divider>
            <div className="tw-w-full tw-mb-8">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700 tw-mb-4  tw-px-4 tw-pb-2 tw-border-b tw-border-gray-200">
                Signatures
              </h3>
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-px-4 tw-w-full">
                <div className="tw-flex tw-flex-col tw-w-full">
                  <span className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mb-2">
                    Client Signature
                  </span>
                  <div className="tw-border tw-border-gray-200 tw-rounded tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-items-center tw-w-full">
                    <img
                      src={getSignatureByType("client")}
                      alt="client signature"
                      className="tw-h-24 tw-object-contain"
                    />
                    <div className="tw-mt-3 tw-text-center tw-flex tw-justify-between tw-items-center tw-w-full">
                      <p className="tw-font-medium tw-text-gray-800 tw-max-w-[50%] tw-break-words tw-whitespace-normal tw-text-left">
                        {formData?.formJson?.clientName || "N/A"}
                      </p>
                      <p className="tw-text-base tw-mt-[2px] tw-font-medium tw-text-gray-500">
                        {formData?.formJson?.signedDate
                          ? convertEpochtoDate(formData?.formJson?.signedDate)
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {formData?.formJson?.amelioStaff?.length > 0 ? (
                  formData.formJson.amelioStaff.map(
                    (staff: any, idx: number) => {
                      const staffId =
                        staff.id ||
                        staff.userID ||
                        staff._id ||
                        staff.staffID ||
                        "";
                      const signature =
                        formData.fileJson.signature.find(
                          (sig: any) =>
                            staffId &&
                            sig.originalname.includes(`${staffId}_sig`)
                        ) || {};
                      return (
                        <div
                          className="tw-flex tw-flex-col tw-w-full"
                          key={staffId || idx}
                        >
                          <span className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mb-2">
                            Engineer Signature ({staff.displayName || "N/A"})
                          </span>
                          <div className="tw-border tw-border-gray-200 tw-rounded tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-items-center tw-w-full">
                            <img
                              src={signature.file || ImageNotAvailable}
                              alt="engineer signature"
                              className="tw-h-24 tw-object-contain"
                            />
                            <div className="tw-mt-3 tw-text-center tw-flex tw-justify-between tw-items-center tw-w-full">
                              <p className="tw-font-medium tw-text-gray-800 tw-max-w-[60%] tw-break-words tw-whitespace-normal tw-text-left">
                                {staff.displayName || "N/A"}
                              </p>
                              <p className="tw-text-base tw-mt-[2px] tw-font-medium tw-text-gray-500">
                                {formData?.formJson?.signedDate
                                  ? convertEpochtoDate(
                                      formData?.formJson?.signedDate
                                    )
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="tw-flex tw-flex-col tw-w-full">
                    <span className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mb-2">
                      Engineer Signature
                    </span>
                    <div className="tw-border tw-border-gray-200 tw-rounded tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-items-center tw-w-full">
                      <img
                        src={ImageNotAvailable}
                        alt="engineer signature"
                        className="tw-h-24 tw-object-contain"
                      />
                      <div className="tw-mt-3 tw-text-center">
                        <p className="tw-font-medium tw-text-gray-800">N/A</p>
                        <p className="tw-text-sm tw-text-gray-500">N/A</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="tw-flex tw-justify-end tw-items-center tw-p-4 tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
            <Button
              className={`tw-flex tw-px-3 tw-py-2 tw-rounded ${
                //   formData?.isReviewed
                //     ? "tw-bg-primary tw-border-primary tw-text-white":
                "!tw-border-[#C3E3F4] tw-text-[#344054]"
              } !tw-border-[#C3E3F4] tw-text-[#344054] tw-font-medium`}
              label={`Back`}
              color="white"
              onClick={() =>
                navigate("/layout/health-and-safety/jobsheet-gloucester")
              }
            />
          </div>
        </div>
      )}
      <ImageModal
        open={modalOpen}
        src={modalImgSrc}
        onClose={handleModalClose}
      />
    </React.Fragment>
  );
};

export default ViewGloucesterForm;
